import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subject, interval, of } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { ConfirmService } from '@core/services/confirm/confirm.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { ApplicationOnCredit, OnlineCreditDocuments } from '@core/services/online-credit/online-credit.model';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';
import { TypeUtilService } from '@core/services/utils/type-util.service';

import { StoreService } from '../store/store.service';

@Component({
    selector: 'app-sing-final-document-pack',
    templateUrl: 'sign-final-document-pack-step.component.html',
    styleUrls: ['./sign-final-document-pack-step.component.scss', '../new-credit.scss'],
    standalone: false
})
export class SignFinalDocumentPackStepComponent implements OnInit, OnDestroy {
  title: string = 'Подпись документов';
  subtitle: string = '';

  public documentsForm: FormGroup = new FormGroup<any>({});
  public signDock: boolean;
  public creditDocuments: OnlineCreditDocuments[];
  public loader$: Observable<boolean>;

  private destroyed$ = new Subject();
  private stoppedInterval$ = new Subject();
  private documentTypes = {
    sch: 'График платежей',
    iss: 'Заявление на выдачу',
    ord: 'Распоряжение',
    trm: 'Индивидуальные условия',
    ins: 'Заявление на страховку',
    inssummary: 'Ключевой информационный документ об условиях добровольного страхования',
    pdn: 'Уводемление о расчете значения показателя долговой нагрузки'
  };
  private fromBackOffice = false;
  private loanApprovedId: string;

  constructor(
    private onlineCreditService: OnlineCreditService,
    private confirmService: ConfirmService,
    private navigationService: NavigationService,
    private stepManager: StepManagerService,
    private store: StoreService,
    private actvatedRoute: ActivatedRoute
  ) {
    this.loader$ = this.stepManager.stepLoaded$;
    this.stepManager.loaderTitle = 'Подготовка документов может занять несколько минут. Пожалуйста, подождите...';
  }

  ngOnInit() {
    this.fromBackOffice = TypeUtilService.isTruthy(this.actvatedRoute.snapshot.queryParams.fromBackOffice);
    this.loanApprovedId = this.actvatedRoute.snapshot.queryParams.loanApprovedId;
    if (!this.fromBackOffice) {
      interval(5000)
        .pipe(
          switchMap(() => this.checkDocumentsAvailable()),
          takeUntil(this.stoppedInterval$)
        )
        .subscribe((res) => {
          const { onlineCreditDocuments } = res;
          this.stoppedInterval$.next(null);
          this.stoppedInterval$.complete();
          this.prepareDocumentsForSign(onlineCreditDocuments);
          this.store.updateApplication(res);
        });
    } else {
      this.prepareDocumentsForSign(this.onlineCreditService.documentsFromBackOffice);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(null);
    this.destroyed$.complete();
    this.stoppedInterval$.next(null);
    this.stoppedInterval$.complete();
  }

  private checkDocumentsAvailable(): Observable<ApplicationOnCredit> {
    return this.onlineCreditService.getApplicationData().pipe(
      filter((cr: ApplicationOnCredit) => {
        return cr.onlineCreditDocuments && cr.onlineCreditDocuments.length > 0;
      })
    );
  }

  private prepareDocumentsForSign(documents: OnlineCreditDocuments[]) {
    documents.forEach((doc) => {
      this.documentsForm.addControl(doc.id, new FormControl(false, [Validators.required, Validators.requiredTrue]));
    });
    this.creditDocuments = [...documents];

    this.stepManager.setStepLoaded(true);
  }

  getDocName(fileName: string) {
    const fileNameToLowerCase = fileName.toLowerCase();
    let readableName = fileName;
    Object.keys(this.documentTypes).forEach((key) => {
      if (fileNameToLowerCase.includes(key)) {
        readableName = this.documentTypes[key];
      }
    });
    // const type = fileNameToLowerCase.split('_')[1].split('.')[0];
    // console.log(type);
    return readableName;
  }

  confirmCredit() {
    this.stepManager.setStepLoaded(false);
    let docIds = Object.keys(this.documentsForm.controls);
    if (this.fromBackOffice) {
      docIds.push(this.loanApprovedId);
    }

    const request$ = this.fromBackOffice
      ? of({ absResponse: 0 })
      : this.onlineCreditService.sendOnlineCreditReady(this.store.value.application.id);
    this.confirmService
      .confirmDocument(docIds, true)
      .pipe(
        switchMap(() => request$),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (res) => {
          if (res && res.hasOwnProperty('absResponse') && res.absResponse === 0) {
            void this.navigationService.push('/new-product/credit/loan-was-issued', { replaceUrl: true });
            // this.stepManager.changeStep(Steps.loanWasIssued)
          } else {
            this.stepManager.showErrorStepAlert();
          }
        },
        (err) => {
          this.stepManager.showErrorStepAlert(err);
        }
      );
  }

  goBack() {
    this.stepManager.setStepLoaded(false);
    void this.navigationService.push('/new-product/credit/loan-calculation', { replaceUrl: true });
    // this.stepManager.changeStep(Steps.calculation);
  }

  abort() {
    this.stepManager.showAbortAlert();
  }
}
